/**
 * @fileoverview Spinner Component
 * @description Inline SVG spinner that is immune to global CSS resets
 *              (including prefers-reduced-motion overrides) because it
 *              uses a scoped <style> tag inside the SVG.
 */

import React from 'react';

const SIZES = { xs: 14, sm: 18, md: 28, lg: 40, xl: 56 };

/**
 * SVG-based spinner that always animates.
 * @param {{ size?: 'xs'|'sm'|'md'|'lg'|'xl'|number, color?: string, thickness?: number }} props
 */
const Spinner = ({ size = 'md', color = 'currentColor', thickness = 3.6 }) => {
  const px = typeof size === 'number' ? size : (SIZES[size] ?? SIZES.md);
  const id = `sp-${React.useId().replace(/:/g, '')}`;

  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 44 44"
      xmlns="http://www.w3.org/2000/svg"
      role="status"
      aria-label="Loading"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      {/*
        Scoped keyframes live inside the SVG, and these animation rules
        are applied via unique id-based selectors with !important, so
        they take precedence over broad external rules such as
        * { animation-duration: 0.01ms !important }.
      */}
      <style>{`
        @keyframes ${id}-rotate {
          100% { transform: rotate(360deg); }
        }
        @keyframes ${id}-dash {
          0%   { stroke-dasharray: 1px, 200px; stroke-dashoffset: 0; }
          50%  { stroke-dasharray: 100px, 200px; stroke-dashoffset: -15px; }
          100% { stroke-dasharray: 100px, 200px; stroke-dashoffset: -125px; }
        }
        #${id}-g {
          animation: ${id}-rotate 1.4s linear infinite !important;
          animation-duration: 1.4s !important;
          animation-iteration-count: infinite !important;
          transform-origin: center;
        }
        #${id}-c {
          animation: ${id}-dash 1.4s ease-in-out infinite !important;
          animation-duration: 1.4s !important;
          animation-iteration-count: infinite !important;
          stroke-dasharray: 80px, 200px;
          stroke-dashoffset: 0;
        }
      `}</style>
      <g id={`${id}-g`}>
        <circle
          id={`${id}-c`}
          cx="22"
          cy="22"
          r="20.2"
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
};

export default Spinner;
